import { router, usePage } from '@inertiajs/react';
import { Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  Spotlight,
  type SpotlightActionData,
  type SpotlightActionGroupData,
  type SpotlightFilterFunction,
} from '@mantine/spotlight';
import {
  IconBell,
  IconBook,
  IconCalendarEvent,
  IconCategory,
  IconChartBar,
  IconFileText,
  IconFileUpload,
  IconInbox,
  IconKey,
  IconMessages,
  IconPlug,
  IconSchool,
  IconSearch,
  IconShield,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { sortBy, uniqBy } from 'lodash';
import { type KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ADMIN_PAGES, ADMIN_TOOLS_GROUP, type AdminEntry } from '@/helpers/adminPages';
import { matchesSearchQuery } from '@/helpers/search';
import { type PatientWithOrganization, usePatientsIndex } from '@/queries/internal_api/patients';
import type { MinimalOrganization, SharedProps } from '@/types';

// Patient actions are prefixed so the patient group can bypass the local filter.
const PATIENT_ACTION_ID_PREFIX = 'patient_';
// The number of patients we keep at a time stays well under the navigation noise.
const PATIENTS_LIMIT = 12;
// Trigger the patient search only once the query is specific enough.
const PATIENTS_SEARCH_MIN_LENGTH = 3;
const SPOTLIGHT_ACTIONS_LIST_ID = 'global-spotlight-actions';

const matchesQuery = (label: string | undefined, query: string) => matchesSearchQuery([label], query);

// Patient results are already filtered by the backend search, so their group skips the local filter.
const isPatientGroup = (item: SpotlightActionData | SpotlightActionGroupData) =>
  'actions' in item &&
  item.actions.length > 0 &&
  item.actions.every((action) => action.id.startsWith(PATIENT_ACTION_ID_PREFIX));

const filterActions: SpotlightFilterFunction = (query, actions) => {
  return actions
    .map((item) => {
      if (!('actions' in item) || isPatientGroup(item)) return item;
      // The group name matches too, so "integrations meudon" finds the entry of that organization.
      return {
        ...item,
        actions: item.actions.filter((action) => matchesSearchQuery([action.label, item.group], query)),
      };
    })
    .filter((item) => {
      if (!('actions' in item)) return matchesQuery(item.label, query);
      return isPatientGroup(item) || item.actions.length > 0;
    });
};

// Admin tools rendered outside Inertia (Avo, background jobs, Blazer) need a full page load.
const visitExternal = (url: string) => {
  window.location.href = url;
};

const adminAction = ({ id, label, icon: Icon }: AdminEntry, onClick: () => void): SpotlightActionData => ({
  id,
  label,
  leftSection: <Icon size={20} stroke={1.5} />,
  onClick,
});

export default function GlobalSpotlight() {
  const { t } = useTranslation();
  const {
    current_organization: currentOrganization,
    current_account: currentAccount,
    internal_docs: internalDocs,
  } = usePage<SharedProps>().props;
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(query, 250);

  if (!currentAccount) throw new Error('Current account not found');

  // Actions that do not depend on an organization, so the spotlight is never empty outside one.
  const globalActions: SpotlightActionData[] = currentAccount.is_pro
    ? [
        {
          id: 'inbox',
          label: t('menu.inbox'),
          leftSection: <IconInbox size={20} stroke={1.5} />,
          onClick: () => router.visit('/pro/conversations'),
        },
        {
          id: 'encounters',
          label: t('menu.activity'),
          leftSection: <IconCalendarEvent size={20} stroke={1.5} />,
          onClick: () => router.visit('/pro/encounters'),
        },
      ]
    : [];

  // Every organization the account works in, current one first, so its pages are reachable from anywhere.
  const organizations = sortBy(
    uniqBy(currentAccount.authorizations, 'organization_id').map((authorization) => authorization.organization),
    [(organization) => organization.id !== currentOrganization?.id, 'name'],
  );

  const organizationActions = (organization: MinimalOrganization): SpotlightActionData[] => [
    {
      id: `${organization.id}_messages`,
      label: t('menu.messages'),
      leftSection: <IconMessages size={20} stroke={1.5} />,
      onClick: () => router.visit(`/pro/conversations?organization_id=${organization.id}`),
    },
    {
      id: `${organization.id}_patients`,
      label: t('menu.patients'),
      leftSection: <IconUsers size={20} stroke={1.5} />,
      onClick: () => router.visit(`/pro/patients?organization_id=${organization.id}`),
    },
    {
      id: `${organization.id}_instructions`,
      label: t('menu.instructions'),
      leftSection: <IconFileText size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/instructions`),
    },
    {
      id: `${organization.id}_document_notification_settings`,
      label: t('document_notification_settings.title'),
      leftSection: <IconSchool size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/document_notification_settings`),
    },
    {
      id: `${organization.id}_reminders`,
      label: t('menu.reminders'),
      leftSection: <IconBell size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/reminder_instructions`),
    },
    {
      id: `${organization.id}_authorizations`,
      label: t('menu.authorizations'),
      leftSection: <IconShield size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/authorizations`),
    },
    {
      id: `${organization.id}_integrations`,
      label: t('integrations.title'),
      leftSection: <IconPlug size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/integrations`),
    },
    {
      id: `${organization.id}_document_classification_settings`,
      label: t('document_classification_settings.title'),
      leftSection: <IconCategory size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/document_classification_settings`),
    },
    {
      id: `${organization.id}_imports`,
      label: t('menu.imports'),
      leftSection: <IconFileUpload size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/imports`),
    },
    {
      id: `${organization.id}_api_keys`,
      label: t('menu.api_keys'),
      leftSection: <IconKey size={20} stroke={1.5} />,
      onClick: () => router.visit(`/organizations/${organization.id}/api_keys`),
    },
    {
      id: `${organization.id}_stats`,
      label: t('menu.stats'),
      leftSection: <IconChartBar size={20} stroke={1.5} />,
      onClick: () => router.visit(`/pro/stats?organization_id=${organization.id}`),
    },
  ];

  // Their organization tells apart entries and namesakes when results span several organizations.
  const hasSeveralOrganizations = organizations.length > 1;

  // Each organization gets its own group, so the same entry stays reachable once per organization.
  const organizationItems: (SpotlightActionData | SpotlightActionGroupData)[] = hasSeveralOrganizations
    ? organizations.map((organization) => ({ group: organization.name, actions: organizationActions(organization) }))
    : organizations.flatMap(organizationActions);

  // Sorted by label, so the pages and the tools outside Inertia read as one list.
  const adminGroup: SpotlightActionGroupData = {
    group: 'Admin',
    actions: sortBy(
      [
        ...ADMIN_PAGES.map((adminPage) => adminAction(adminPage, () => router.visit(adminPage.href))),
        ...ADMIN_TOOLS_GROUP.entries.map((adminTool) => adminAction(adminTool, () => visitExternal(adminTool.href))),
      ],
      'label',
    ),
  };

  // Each internal doc is its own entry, so a super admin reaches it by typing its title.
  const internalDocsGroup: SpotlightActionGroupData = {
    group: 'Internal Docs',
    actions: internalDocs.map((internalDoc) => ({
      id: `internal_doc_${internalDoc.slug}`,
      label: internalDoc.title,
      leftSection: <IconBook size={20} stroke={1.5} />,
      onClick: () => router.visit(`/admin/internal_docs/${internalDoc.slug}`),
    })),
  };

  const isSuperAdmin = currentAccount.is_super_admin;
  // Ungrouped actions come first: rendered after a group they would read as part of it.
  const menuItems: (SpotlightActionData | SpotlightActionGroupData)[] = [
    ...globalActions,
    ...organizationItems,
    ...(isSuperAdmin ? [adminGroup] : []),
    ...(internalDocsGroup.actions.length > 0 ? [internalDocsGroup] : []),
  ];

  // Fall back to a patient search only when no menu item matches the (trimmed) query.
  const qualifiesForPatientSearch = (text: string) => {
    const trimmed = text.trim();
    return (
      currentAccount.is_pro &&
      trimmed.length >= PATIENTS_SEARCH_MIN_LENGTH &&
      filterActions(trimmed, menuItems).length === 0
    );
  };

  // The actual search runs on the debounced query, but the loader follows the live query so it shows
  // immediately while the debounce and request settle.
  const trimmedQuery = debouncedQuery.trim();
  const shouldSearchPatients = qualifiesForPatientSearch(debouncedQuery);

  // No organization is passed: the search covers the patients of every organization the account has
  // access to, so a patient is reachable from any page.
  const { data: patients, isFetching: isFetchingPatients } = usePatientsIndex(trimmedQuery, {
    enabled: shouldSearchPatients,
  });

  // What tells namesakes apart, read right under the name rather than across the row.
  const patientDetails = (patient: PatientWithOrganization) =>
    [
      patient.birthdate ? dayjs(patient.birthdate).format('L') : null,
      hasSeveralOrganizations ? patient.organization.name : null,
    ]
      .filter(Boolean)
      .join(' · ');

  const patientGroup: SpotlightActionGroupData | null =
    shouldSearchPatients && patients && patients.length > 0
      ? {
          group: t('spotlight.patients_group'),
          actions: patients.slice(0, PATIENTS_LIMIT).map((patient) => ({
            id: `${PATIENT_ACTION_ID_PREFIX}${patient.id}`,
            label: patient.name,
            description: patientDetails(patient),
            leftSection: <IconUser size={20} stroke={1.5} />,
            onClick: () => router.visit(`/pro/patients/${patient.id}`),
          })),
        }
      : null;

  const actions: (SpotlightActionData | SpotlightActionGroupData)[] = [
    ...menuItems,
    ...(patientGroup ? [patientGroup] : []),
  ];

  if (actions.length === 0) return null;

  // Show the loader in the search bar while the debounce and request settle, so it stays visible
  // regardless of the results dropdown state.
  const isSearchingPatients = qualifiesForPatientSearch(query) && (query !== debouncedQuery || isFetchingPatients);
  const hasSpotlightResults = filterActions(query, actions).length > 0;

  const handleSearchKeyDownCapture = (event: KeyboardEvent<HTMLInputElement>) => {
    const isEnterKey = event.nativeEvent.code === 'Enter' || event.nativeEvent.code === 'NumpadEnter';

    if (!isEnterKey) return;

    const selectedAction = document.querySelector(`#${SPOTLIGHT_ACTIONS_LIST_ID} [data-selected]`);

    if (!hasSpotlightResults || !selectedAction) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <Spotlight
      actions={actions}
      query={query}
      onQueryChange={setQuery}
      shortcut='mod + K'
      filter={filterActions}
      nothingFound={t('spotlight.nothing_found')}
      scrollAreaProps={{ id: SPOTLIGHT_ACTIONS_LIST_ID }}
      searchProps={{
        leftSection: <IconSearch size={20} stroke={1.5} />,
        rightSection: isSearchingPatients ? <Loader size='sm' /> : null,
        placeholder: t('spotlight.placeholder'),
        onKeyDownCapture: handleSearchKeyDownCapture,
      }}
    />
  );
}
